import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto p-6 md:p-10">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-900">← Home</Link>
        <h1 className="text-3xl md:text-4xl font-black mt-4">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mt-1">Safety Onsite Solutions, Inc. · Last updated February 7, 2026</p>

        <div className="mt-8 prose prose-slate max-w-none text-slate-700 space-y-6 leading-relaxed">
          <section>
            <h2 className="text-xl font-bold text-slate-900">1. Who we are</h2>
            <p>
              Safety Onsite Solutions, Inc. ("Safety Onsite Solutions," "we,"
              "us," or "our") provides on-site safety staffing services to
              construction clients. This Privacy Policy describes how we
              collect, use, and disclose information when contractors, client
              supervisors, or administrators interact with our staffing
              operations platform, including its email and SMS notification
              features.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">2. Information we collect</h2>
            <p>We collect the following categories of information:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><b>Contact information</b> — names, mobile phone numbers, and email addresses of contractors we employ and client-side supervisors who approve their timesheets.</li>
              <li><b>Employment information</b> — pay rates, ACH banking details, timesheets, per-diem days, and expense exceptions provided by contractors.</li>
              <li><b>Client and project information</b> — client company details, project locations, purchase order numbers, and billing information.</li>
              <li><b>Usage and log data</b> — timestamps of logins, invoice sends, timesheet submissions, and supervisor approvals collected for audit and compliance purposes.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">3. How we use SMS text messages</h2>
            <p>
              We use SMS text messages to notify designated site supervisors
              when a contractor assigned to their project has submitted a
              weekly timesheet awaiting approval, and to send reminder
              messages if approval is delayed. Messages contain a link to a
              secure approval page and are typically 1–2 per week per assigned
              project.
            </p>
            <p>
              <b>Enrollment is voluntary and never required to work with us
              or to approve timesheets.</b> Supervisors may enroll themselves at
              any time by visiting{" "}
              <Link to="/sms-signup" className="text-green-700 hover:underline">
                app.safetyonsitesolutions.com/sms-signup
              </Link>{" "}
              and completing the opt-in form, which requires explicit
              affirmative consent via an unchecked checkbox. Alternatively,
              our client companies may enroll their designated supervisors
              after obtaining their verbal or written consent at project
              kickoff — in every case the supervisor retains the right to
              opt out at any time.
            </p>
            <p>
              Recipients may opt out at any time by replying <b>STOP</b> to
              any message. Approval by email link remains available whether
              or not a supervisor is enrolled in SMS. Message and data rates
              may apply.
            </p>
            <p>
              We <b>do not</b> use SMS for marketing purposes, promotions, or
              any content unrelated to timesheet approval workflow.
              <b> We do not sell, rent, or share mobile phone numbers or SMS
              opt-in consent with third parties or affiliates for marketing
              purposes.</b>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">4. How we use email</h2>
            <p>
              We send transactional emails for timesheet approval requests,
              invoice delivery, payment reminders, password resets, and other
              operational notifications. We do not use email addresses
              collected through this platform for marketing purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">5. How we protect data</h2>
            <p>
              All data is transmitted over encrypted HTTPS connections and
              stored in access-restricted databases. Sensitive fields such as
              ACH banking numbers are stored using field-level encryption.
              Access to administrative functions is restricted to authorized
              company personnel using role-based access controls.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">6. Data retention</h2>
            <p>
              We retain personal and payroll information for the period
              required by applicable federal and state tax, employment, and
              record-keeping laws (typically seven years for payroll records).
              After that period, data is securely deleted or de-identified.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">7. Third-party service providers</h2>
            <p>We rely on the following service providers to operate the platform:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><b>Twilio</b> — delivery of SMS text messages.</li>
              <li><b>Resend</b> — delivery of transactional email.</li>
              <li><b>MongoDB Atlas / Emergent</b> — application hosting and database.</li>
            </ul>
            <p>
              These providers process data solely on our behalf under their
              respective terms of service and privacy policies. We do not
              authorize them to use platform data for any purpose other than
              providing services to us.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">8. Your choices</h2>
            <p>
              You may unsubscribe from SMS notifications at any time by
              replying <b>STOP</b> to any message. You may unsubscribe from
              transactional email by contacting us directly at
              billing@safetyonsitesolutions.com. You may request access,
              correction, or deletion of your personal information by
              contacting us using the details below.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">9. Contact</h2>
            <p>
              Questions about this Privacy Policy or our data practices should
              be directed to:
            </p>
            <p className="pl-4 border-l-2 border-slate-200">
              <b>Safety Onsite Solutions, Inc.</b><br />
              Email: billing@safetyonsitesolutions.com<br />
              Phone: (740) 550-9402
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">10. Changes</h2>
            <p>
              We may update this Privacy Policy from time to time. Material
              changes will be reflected by updating the "Last updated" date at
              the top of this page. Continued use of the platform after any
              such change constitutes acceptance of the updated policy.
            </p>
          </section>
        </div>

        <div className="mt-10 text-sm text-slate-400 border-t border-slate-200 pt-4">
          <Link to="/terms" className="hover:text-slate-700">Terms &amp; Conditions</Link>
          <span className="mx-2">·</span>
          <Link to="/" className="hover:text-slate-700">Home</Link>
        </div>
      </div>
    </div>
  );
}
